import { useRef, useEffect } from 'react';

/**
 * HeroCanvas — Particle network with connecting lines + gradient orbs.
 * Theme-aware: uses darker particles in light mode for visibility.
 * Mouse interaction: particles repel, lines connect to cursor.
 */
function HeroCanvas() {
  var canvasRef = useRef(null);

  useEffect(function () {
    var canvas = canvasRef.current;
    if (!canvas) return;

    var prefersReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    var ctx = canvas.getContext('2d');
    var animId;
    var mouse = { x: -1000, y: -1000 };
    var dpr = Math.min(window.devicePixelRatio || 1, 2);
    var w = 0;
    var h = 0;
    var particles = [];
    var time = 0;

    function isLightTheme() {
      return document.documentElement.getAttribute('data-theme') === 'light';
    }

    // Color config per theme
    function getColors() {
      if (isLightTheme()) {
        return {
          particle: [200, 120, 60],
          particleOpacityMin: 0.25,
          particleOpacityMax: 0.45,
          lineAlphaMultiplier: 0.2,
          mouseLineAlpha: 0.25,
          orbColors: [
            { color: [212, 106, 40], opacity: 0.08 },
            { color: [200, 120, 60], opacity: 0.06 },
            { color: [212, 106, 40], opacity: 0.07 },
          ],
        };
      }
      return {
        particle: [232, 140, 70],
        particleOpacityMin: 0.25,
        particleOpacityMax: 0.55,
        lineAlphaMultiplier: 0.2,
        mouseLineAlpha: 0.3,
        orbColors: [
          { color: [232, 123, 53], opacity: 0.14 },
          { color: [240, 160, 96], opacity: 0.09 },
          { color: [232, 123, 53], opacity: 0.11 },
        ],
      };
    }

    var orbs = [
      { cx: 0.25, cy: 0.4, rx: 0.15, ry: 0.2, speed: 0.0004, phase: 0 },
      { cx: 0.7, cy: 0.3, rx: 0.12, ry: 0.18, speed: 0.0005, phase: 2.5 },
      { cx: 0.5, cy: 0.7, rx: 0.18, ry: 0.12, speed: 0.0003, phase: 4.5 },
    ];

    function resize() {
      var rect = canvas.parentElement.getBoundingClientRect();
      w = rect.width;
      h = rect.height;
      canvas.width = w * dpr;
      canvas.height = h * dpr;
      canvas.style.width = w + 'px';
      canvas.style.height = h + 'px';
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    }

    function createParticles() {
      var colors = getColors();
      var count = Math.min(Math.floor((w * h) / 8000), 90);
      particles = [];
      for (var i = 0; i < count; i++) {
        particles.push({
          x: Math.random() * w,
          y: Math.random() * h,
          vx: (Math.random() - 0.5) * 0.45,
          vy: (Math.random() - 0.5) * 0.45,
          r: Math.random() * 1.6 + 0.8,
          opacity: Math.random() * (colors.particleOpacityMax - colors.particleOpacityMin) + colors.particleOpacityMin,
        });
      }
    }

    function draw() {
      time++;
      var colors = getColors();
      var pc = colors.particle;
      ctx.clearRect(0, 0, w, h);

      // Layer 1: Gradient orbs
      for (var o = 0; o < orbs.length; o++) {
        var orb = orbs[o];
        var oc = colors.orbColors[o];
        var t = time * orb.speed + orb.phase;
        var ox = (orb.cx + Math.sin(t) * orb.rx) * w;
        var oy = (orb.cy + Math.cos(t * 0.7) * orb.ry) * h;
        var radius = w * 0.2;

        var grad = ctx.createRadialGradient(ox, oy, 0, ox, oy, radius);
        grad.addColorStop(0, 'rgba(' + oc.color.join(',') + ',' + oc.opacity + ')');
        grad.addColorStop(0.5, 'rgba(' + oc.color.join(',') + ',' + (oc.opacity * 0.4) + ')');
        grad.addColorStop(1, 'rgba(' + oc.color.join(',') + ',0)');
        ctx.fillStyle = grad;
        ctx.fillRect(0, 0, w, h);
      }

      // Layer 2: Particles
      var maxDist = 130;
      for (var i = 0; i < particles.length; i++) {
        var p = particles[i];
        p.x += p.vx;
        p.y += p.vy;
        if (p.x < 0) p.x = w;
        if (p.x > w) p.x = 0;
        if (p.y < 0) p.y = h;
        if (p.y > h) p.y = 0;

        // Mouse repulsion
        var dx = p.x - mouse.x;
        var dy = p.y - mouse.y;
        var dist = Math.sqrt(dx * dx + dy * dy);
        if (dist < 160 && dist > 0) {
          var force = (160 - dist) / 160;
          p.x += (dx / dist) * force * 1.5;
          p.y += (dy / dist) * force * 1.5;
        }

        ctx.beginPath();
        ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
        ctx.fillStyle = 'rgba(' + pc.join(',') + ',' + p.opacity + ')';
        ctx.fill();
      }

      // Layer 3: Connections between particles
      for (var a = 0; a < particles.length; a++) {
        for (var b = a + 1; b < particles.length; b++) {
          var ddx = particles[a].x - particles[b].x;
          var ddy = particles[a].y - particles[b].y;
          var d = Math.sqrt(ddx * ddx + ddy * ddy);
          if (d < maxDist) {
            var alpha = (1 - d / maxDist) * colors.lineAlphaMultiplier;
            ctx.beginPath();
            ctx.moveTo(particles[a].x, particles[a].y);
            ctx.lineTo(particles[b].x, particles[b].y);
            ctx.strokeStyle = 'rgba(' + pc.join(',') + ',' + alpha + ')';
            ctx.lineWidth = 0.7;
            ctx.stroke();
          }
        }
      }

      // Layer 4: Mouse-to-particle connections
      if (mouse.x > 0 && mouse.y > 0) {
        for (var m = 0; m < particles.length; m++) {
          var mdx = particles[m].x - mouse.x;
          var mdy = particles[m].y - mouse.y;
          var md = Math.sqrt(mdx * mdx + mdy * mdy);
          if (md < 200) {
            var ma = (1 - md / 200) * colors.mouseLineAlpha;
            ctx.beginPath();
            ctx.moveTo(mouse.x, mouse.y);
            ctx.lineTo(particles[m].x, particles[m].y);
            ctx.strokeStyle = 'rgba(' + pc.join(',') + ',' + ma + ')';
            ctx.lineWidth = 0.8;
            ctx.stroke();
          }
        }

        // Cursor glow
        var glowGrad = ctx.createRadialGradient(mouse.x, mouse.y, 0, mouse.x, mouse.y, 120);
        glowGrad.addColorStop(0, 'rgba(' + pc.join(',') + ', 0.1)');
        glowGrad.addColorStop(1, 'rgba(' + pc.join(',') + ', 0)');
        ctx.fillStyle = glowGrad;
        ctx.beginPath();
        ctx.arc(mouse.x, mouse.y, 120, 0, Math.PI * 2);
        ctx.fill();
      }

      if (!prefersReduced) {
        animId = requestAnimationFrame(draw);
      }
    }

    function handleMouse(e) {
      var rect = canvas.getBoundingClientRect();
      mouse.x = e.clientX - rect.left;
      mouse.y = e.clientY - rect.top;
    }
    function handleMouseLeave() {
      mouse.x = -1000;
      mouse.y = -1000;
    }

    // Watch for theme changes
    var themeObserver = new MutationObserver(function () {
      createParticles();
    });
    themeObserver.observe(document.documentElement, { attributes: true, attributeFilter: ['data-theme'] });

    resize();
    createParticles();
    draw();

    var resizeHandler = function () { resize(); createParticles(); };
    window.addEventListener('resize', resizeHandler);
    canvas.addEventListener('mousemove', handleMouse);
    canvas.addEventListener('mouseleave', handleMouseLeave);

    return function () {
      cancelAnimationFrame(animId);
      themeObserver.disconnect();
      window.removeEventListener('resize', resizeHandler);
      canvas.removeEventListener('mousemove', handleMouse);
      canvas.removeEventListener('mouseleave', handleMouseLeave);
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      style={{
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        pointerEvents: 'auto',
        zIndex: 0,
      }}
      aria-hidden="true"
    />
  );
}

export default HeroCanvas;
